import { LoadPanel, TextBox } from "devextreme-react";
import { Button } from "devextreme-react/button";
import {
  Column,
  DataGrid,
  FilterRow,
  Item,
  Toolbar,
} from "devextreme-react/data-grid";
import { Popup } from "devextreme-react/popup";
import { CustomStore, DataSource } from "devextreme/common/data";
import { ColumnButtonClickEvent } from "devextreme/ui/data_grid";
import { confirm } from "devextreme/ui/dialog";
import notify from "../../lib/notify";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getProjectEmployees,
  unassignEmployeeToProject,
} from "../../api/projects";
import { CEID, Project, ProjectSupervisor } from "../../types";
import { useScreenSize } from "../../utils/media-query";
import ProjectSupervisorAssignListPopup from "../project-supervisor-assign-list-popup/ProjectSupervisorAssignListPopup";

type ProjectSupervisorsListPopupProps = {
  project: Project;
  onClose: () => void;
};

export default function ProjectSupervisorsListPopup({
  project,
  onClose,
}: ProjectSupervisorsListPopupProps) {
  const { t } = useTranslation();
  const { isXSmall, isSmall } = useScreenSize();
  const isMobile = isXSmall || isSmall;

  const [showSupervisorAssignPopup, setShowSupervisorAssignPopup] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [mobileEmployees, setMobileEmployees] = useState<ProjectSupervisor[]>(
    [],
  );
  const [reloadCounter, setReloadCounter] = useState(0);

  const dataSource = useMemo(() => {
    return new DataSource({
      store: new CustomStore({
        loadMode: "raw",
        key: ["companyId", "employeeId"],
        load: async () => await getProjectEmployees(project.id),
      }),
    });
  }, [project.id]);

  useEffect(() => {
    if (!isMobile) return;
    let cancelled = false;
    (async () => {
      try {
        const employees = await getProjectEmployees(project.id);
        if (!cancelled) setMobileEmployees(employees ?? []);
      } catch {
        if (!cancelled) setMobileEmployees([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [project.id, isMobile, reloadCounter]);

  const filteredMobileEmployees = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return mobileEmployees;
    return mobileEmployees.filter((e: any) =>
      (e.fullName ?? "").toLowerCase().includes(q),
    );
  }, [mobileEmployees, filter]);

  const handleProjectSupervisorUnassign = useCallback(
    async (projectId: number, ceid: CEID) => {
      setIsLoading(true);

      try {
        await unassignEmployeeToProject(projectId, ceid);
        dataSource.reload();
        setReloadCounter((c) => c + 1);

        notify(
          t("projectSupervisorsListPopup.supervisorToProjectAssignmentRemoved"),
          "success",
          5000,
        );
      } catch (error) {
        notify(
          error instanceof Error
            ? error.message
            : t("common.unknownErrorOccurred"),
          "error",
          5000,
        );
      } finally {
        setIsLoading(false);
      }
    },
    [dataSource, t],
  );

  const confirmRemove = useCallback(
    async (employee: ProjectSupervisor) => {
      const decision = await confirm(
        t(
          "projectSupervisorsListPopup.areYouSureYouWantToRemoveSupervisorToProjectAssignment",
          {
            employeeFullName: employee.fullName,
            projectAcronym: project.acronym,
          },
        ),
        t("common.confirmation"),
      );

      if (decision === true) {
        await handleProjectSupervisorUnassign(project.id, {
          companyId: employee.companyId,
          employeeId: employee.employeeId,
        });
      }
    },
    [project.id, project.acronym, handleProjectSupervisorUnassign, t],
  );

  const handleProjectSupervisorRemoveButtonClick = useCallback(
    async (event: ColumnButtonClickEvent<any, any>) => {
      const employee = event.row?.data as ProjectSupervisor;
      await confirmRemove(employee);
    },
    [confirmRemove],
  );

  return (
    <>
      {isLoading ? (
        <LoadPanel
          shading
          shadingColor="rgba(0, 0, 0, 0.3)"
          visible
          message={t("common.processingRequest")}
        />
      ) : null}
      <Popup
        wrapperAttr={{ class: "projects-popup" }}
        visible
        title={t("projectSupervisorsListPopup.title", {
          projectAcronym: project.acronym,
        })}
        onHiding={onClose}
        fullScreen={isMobile}
        maxWidth={isMobile ? undefined : 800}
        height={isMobile ? "100%" : "auto"}
        maxHeight={isMobile ? undefined : "80vh"}
        showCloseButton
        dragEnabled={!isMobile}
      >
        {showSupervisorAssignPopup ? (
          <ProjectSupervisorAssignListPopup
            project={project}
            onClose={(refresh) => {
              setShowSupervisorAssignPopup(false);

              if (refresh) {
                dataSource.reload();
                setReloadCounter((c) => c + 1);
              }
            }}
          />
        ) : null}
        {isMobile ? (
          <>
            <div className="projects-popup-mobile-filter">
              <TextBox
                value={filter}
                onValueChanged={(e) => setFilter(e.value ?? "")}
                placeholder={t("searchPlaceholder")}
                showClearButton
              />
            </div>
            <Button
              text={t("project.addManager")}
              type="default"
              width="100%"
              onClick={() => setShowSupervisorAssignPopup(true)}
            />
            <div
              className="projects-popup-mobile-list"
              style={{ marginTop: 12 }}
            >
              {filteredMobileEmployees.length === 0 ? (
                <div className="projects-mobile-empty">{t("No data")}</div>
              ) : (
                filteredMobileEmployees.map((e: any) => (
                  <div
                    key={`${e.companyId}-${e.employeeId}`}
                    className="projects-popup-mobile-card"
                  >
                    <div className="projects-popup-mobile-card-name">
                      {e.fullName}
                    </div>
                    <Button
                      icon="trash"
                      stylingMode="text"
                      type="danger"
                      hint={t("project.removeEmployee")}
                      onClick={() => confirmRemove(e)}
                    />
                  </div>
                ))
              )}
            </div>
          </>
        ) : (
          <DataGrid
            dataSource={dataSource}
            showBorders
            allowColumnResizing
            allowColumnReordering
            searchPanel={{
              visible: true,
              placeholder: t("searchPlaceholder"),
            }}
            paging={{
              pageSize: 10,
            }}
            pager={{
              visible: true,
              showInfo: true,
              showNavigationButtons: true,
            }}
          >
            <Toolbar>
              <Item
                name="searchPanel"
                location="before"
                locateInMenu="auto"
                cssClass="datagrid-search-panel-without-margin-left"
              />
              ,
              <Item location="after" locateInMenu="auto">
                <Button
                  onClick={() => setShowSupervisorAssignPopup(true)}
                  text={t("project.addManager")}
                  type="default"
                />
              </Item>
            </Toolbar>
            <FilterRow visible />
            <Column
              dataField="fullName"
              dataType="string"
              caption={t("project.employee")}
            />
            <Column
              alignment="right"
              type="buttons"
              buttons={[
                {
                  icon: "trash",
                  hint: t("project.removeEmployee"),
                  onClick: handleProjectSupervisorRemoveButtonClick,
                },
              ]}
            />
          </DataGrid>
        )}
      </Popup>
    </>
  );
}
